import { useState, useCallback, useRef, RefObject, useEffect } from 'react'
import { useTheme, Theme } from '@emotion/react'

import { useSpring, SpringValue } from 'react-spring'

interface Hooks {
  isPressed: boolean
  handlePressed: () => void
  hideMenu: () => void
  backgroundSpring: Record<string, SpringValue>
  menuListSpring: Record<string, SpringValue>
  containerRef: RefObject<HTMLDivElement>
  theme: Theme
}

export function useHooks(menuLength: number): Hooks {
  const theme = useTheme()
  const containerRef = useRef<HTMLDivElement>(null)

  const [isPressed, togglePressed] = useState(false)

  const handlePressed = useCallback(() => {
    const body = document.querySelector('body')
    if (body) {
      if (isPressed) {
        body.style.overflow = 'auto'
      } else {
        body.style.overflow = 'hidden'
      }
    }

    togglePressed(!isPressed)
  }, [isPressed])

  const hideMenu = useCallback(() => {
    const body = document.querySelector('body')
    if (body) {
      body.style.overflow = 'auto'
    }

    togglePressed(false)
  }, [])

  const resizeCallback = useCallback(() => {
    if (containerRef.current) {
      if (!isPressed) return

      const width = containerRef.current.getBoundingClientRect().width
      if (width > theme.size.bp[2]) {
        hideMenu()
      }
    }
  }, [isPressed, containerRef.current])

  useEffect(() => {
    window.addEventListener('resize', resizeCallback)

    return () => window.removeEventListener('resize', resizeCallback)
  }, [resizeCallback])

  const backgroundSpring = useSpring({
    from: {
      opacity: 0,
    },
    to: {
      opacity: isPressed ? 0.79 : 0,
    },
    config: {
      mass: 1,
      tension: 300,
      friction: 30,
    },
  })

  const menuListSpring = useSpring({
    from: {
      height: 0,
    },
    to: {
      height: isPressed
        ? (menuLength + 2) * parseInt(theme.size.height[400])
        : 0,
    },
    config: {
      mass: 1,
      tension: 300,
      friction: 30,
    },
  })

  return {
    handlePressed,
    hideMenu,
    isPressed,
    backgroundSpring,
    menuListSpring,
    containerRef,
    theme,
  }
}
